// src/app/login.jsx
import React, { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  KeyboardAvoidingView,
  ScrollView,
  Keyboard,
  Platform,
} from 'react-native';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../apiConfig.js';

export default function Login({ setIsAuthenticated }) {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();

  const handleChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSignIn = async () => {
    setErrorMessage('');
    try {
      const { data } = await axios.post(`${API_BASE_URL}/login`, {
        email: formData.email.trim(),
        password: formData.password,
      });

      if (data?.code === 'INVALID_CREDENTIALS') {
        alert('Invalid email or password. Please check your credentials and try again.');
        return;
      }
      if (data?.code === 'PENDING_CONFIRMATION') {
        alert('Your email confirmation is still pending. Please check your email.');
        navigate('/login', { replace: true });
        return;
      }
      if (data?.code === 'NEW_TOKEN_SENT') {
        alert('A new confirmation email has been sent. Please confirm your email.');
        navigate('/login', { replace: true });
        return;
      }
      if (data?.token && data?.account) {
        // Store the JWT token
        localStorage.setItem('userToken', data.token);
        
        // Store user permissions and account info
        const userPermissions = {
          user_type: data.account.user_type || '',
          job_perm: data.account.job_perm || '',
          invoice_perm: data.account.invoice_perm || '',
          quote_perm: data.account.quote_perm || '',
          customer_perm: data.account.customer_perm || '',
          price_perm: data.account.price_perm || '',
          userId: data.account.id,
          email: data.account.email,
          firstName: data.account.firstName || '',
          lastName: data.account.lastName || ''
        };
        
        localStorage.setItem('userPermissions', JSON.stringify(userPermissions));
        
        // Update authentication state
        setIsAuthenticated(true);
        navigate('/', { replace: true });
        return;
      }

      alert('Failed to log in. Please try again.');
    } catch (error) {
      console.error('Error during login:', error);
      setErrorMessage(error?.response?.data?.error || 'Invalid email or password. Please try again.');
      alert('Failed to log in. Please try again later.');
    }
  };

  return (
    <div style={styles.pageContainer}>
      <div style={styles.backgroundOverlay}>
        <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
          <ScrollView
            contentContainerStyle={{ flexGrow: 1, justifyContent: 'center', alignItems: 'center', padding: 20 }}
            keyboardShouldPersistTaps="handled"
          >
            <View style={styles.container}>
              <View style={styles.logoContainer}>
                <Text style={styles.logoText}>TradeBook</Text>
                <Text style={styles.logoSubtext}>Business Management</Text>
              </View>

              <Text style={styles.header}>Welcome Back</Text>
              <Text style={styles.subheader}>Sign in to your account</Text>

              {errorMessage ? (
                <View style={styles.errorContainer}>
                  <Text style={styles.error}>{errorMessage}</Text>
                </View>
              ) : null}

              <View style={styles.inputContainer}>
                <TextInput
                  style={styles.input}
                  placeholder="Email address"
                  placeholderTextColor="#9CA3AF"
                  value={formData.email}
                  onChangeText={(text) => handleChange('email', text)}
                  autoCapitalize="none"
                  autoCorrect={false}
                  keyboardType="email-address"
                  textContentType="emailAddress"
                />
              </View>

              <View style={styles.inputContainer}>
                <TextInput
                  style={styles.input}
                  placeholder="Password"
                  placeholderTextColor="#9CA3AF"
                  secureTextEntry
                  value={formData.password}
                  onChangeText={(text) => handleChange('password', text)}
                  textContentType="password"
                />
              </View>

              <TouchableOpacity style={styles.primaryButton} onPress={handleSignIn}>
                <Text style={styles.primaryButtonText}>Sign In</Text>
              </TouchableOpacity>

              <View style={styles.divider}>
                <View style={styles.dividerLine} />
                <Text style={styles.dividerText}>or</Text>
                <View style={styles.dividerLine} />
              </View>

              <TouchableOpacity
                style={styles.secondaryButton}
                onPress={() => navigate('/create-account')}
              >
                <Text style={styles.secondaryButtonText}>Create New Account</Text>
              </TouchableOpacity>

              <TouchableOpacity onPress={() => navigate('/forgot')} style={styles.forgotPasswordContainer}>
                <Text style={styles.linkText}>Forgot your password?</Text>
              </TouchableOpacity>
            </View>
          </ScrollView>
        </KeyboardAvoidingView>
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  pageContainer: {
    flex: 1,
    backgroundColor: '#f0f4f8',
  },
  backgroundOverlay: {
    flex: 1,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
  },
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40, // Increased padding
    marginTop: 20,
    backgroundColor: '#fff',
    borderRadius: 10,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 5,
    elevation: 5,
    maxWidth: 600, // Made much wider - was default full width
    width: '90%', // Responsive width that takes up 90% of screen
    alignSelf: 'center', // Center the container horizontally
  },
  logoContainer: {
    alignItems: 'center',
    marginBottom: 30, // Increased spacing
  },
  logoText: {
    fontSize: 36, // Increased font size
    fontWeight: 'bold',
    color: '#007BFF',
  },
  logoSubtext: {
    fontSize: 18, // Increased font size
    color: '#6B7280',
  },
  header: { 
    fontSize: 28, // Increased font size
    fontWeight: 'bold', 
    marginBottom: 12 
  },
  subheader: { 
    fontSize: 18, // Increased font size
    color: '#6B7280', 
    marginBottom: 30 // Increased spacing
  },
  inputContainer: {
    width: '100%',
    marginBottom: 20, // Increased spacing
  },
  input: {
    width: '100%',
    padding: 15, // Increased padding
    borderWidth: 1,
    borderColor: '#ccc',
    borderRadius: 8, // Larger border radius
    backgroundColor: '#F9FAFB',
    fontSize: 16, // Increased font size
  },
  errorContainer: {
    width: '100%',
    marginBottom: 20, // Increased spacing
    padding: 15, // Increased padding
    backgroundColor: '#FEE2E2',
    borderRadius: 8, // Larger border radius
  },
  error: { 
    color: '#B91C1C',
    fontSize: 16, // Increased font size
  },
  primaryButton: {
    backgroundColor: '#007BFF',
    padding: 15, // Increased padding
    width: '100%',
    borderRadius: 8, // Larger border radius
    marginBottom: 20, // Increased spacing
  },
  primaryButtonText: { 
    color: '#fff', 
    fontSize: 18, // Increased font size
    textAlign: 'center',
    fontWeight: 'bold',
  },
  divider: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: 25, // Increased spacing
    width: '100%',
  },
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: '#E5E7EB',
  },
  dividerText: {
    marginHorizontal: 15, // Increased spacing
    fontSize: 16, // Increased font size
    color: '#6B7280',
  },
  secondaryButton: {
    backgroundColor: '#34C759',
    padding: 15, // Increased padding
    width: '100%',
    borderRadius: 8, // Larger border radius
    marginBottom: 20, // Increased spacing
  },
  secondaryButtonText: { 
    color: '#fff', 
    fontSize: 18, // Increased font size
    textAlign: 'center',
    fontWeight: 'bold',
  },
  forgotPasswordContainer: {
    marginTop: 15, // Increased spacing
  },
  linkText: {
    color: '#007BFF',
    fontSize: 16,
    textDecorationLine: 'underline',
  },
});
